import { memo } from 'react';
import { Badge, Box, Button, Container, Link, SpaceBetween } from '@amzn/awsui-components-core';
import { KnowledgeSource } from '@amzn/awscat-pattern-library-service-model';

/** A single duplicate-scan match shown as a card in {@link DuplicateScanResultsView}. */
export interface DuplicateScanResultItem {
  readonly id: string;
  readonly title: string;
  readonly url: string;
  /** Knowledge source of the match; omit when the scan source is unknown (e.g. publisher review). */
  readonly source?: KnowledgeSource;
  readonly similarityMatch: number;
  /** Explanation of why the match was flagged; omit when the scan provides no reason. */
  readonly whyFlagged?: string;
  readonly canRequestContributor?: boolean;
  readonly authorEmails?: string[];
}

interface DuplicateScanResultCardViewProps {
  readonly item: DuplicateScanResultItem;
  readonly onRequestContributor?: (itemId: string) => void;
  /** Called when the user clicks through to the matched article; consumers wire their own telemetry. */
  readonly onArticleClick?: (itemId: string) => void;
}

function mapKnowledgeSourceToDisplaySource(source: KnowledgeSource): string {
  switch (source) {
    case KnowledgeSource.APGL:
      return 'APG Library';
    case KnowledgeSource.AWS:
      return 'AWS Service Documentation';
    default:
      return 'Unknown';
  }
}

export const DuplicateScanResultCardView = memo(function DuplicateScanResultCardView({
  item,
  onRequestContributor,
  onArticleClick,
}: DuplicateScanResultCardViewProps) {
  const isExternalLink = item.url.startsWith('https');

  return (
    <Box data-testid={`duplicate-scan-result-card-${item.id}`} margin={{ bottom: 's' }}>
      <Container>
        <SpaceBetween size="xs">
          {/* Header row: Title with external link on left, badge on right */}
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <Box>
              {isExternalLink ? (
                <Link
                  href={item.url}
                  external
                  fontSize="heading-s"
                  onFollow={() => onArticleClick?.(item.id)}
                >
                  {item.title}
                </Link>
              ) : (
                <Box variant="h4">{item.title}</Box>
              )}
            </Box>
            <Badge color="severity-low">Similarity match ({item.similarityMatch}%)</Badge>
          </div>

          {/* Source line (only when the scan provides a source) */}
          {item.source !== undefined && (
            <Box fontSize="body-s">
              <Box variant="span" fontWeight="bold">
                Source:{' '}
              </Box>
              <Box variant="span" color="text-body-secondary">
                {mapKnowledgeSourceToDisplaySource(item.source)}
              </Box>
            </Box>
          )}

          {/* Why flagged section (only when the scan provides a reason) */}
          {item.whyFlagged && (
            <Box fontSize="body-s" color="text-body-secondary">
              <Box variant="span" fontWeight="bold">
                Why flagged:{' '}
              </Box>
              {item.whyFlagged}
            </Box>
          )}

          {/* Request contributor button (shown when canRequestContributor is true) */}
          {item.canRequestContributor && (
            <Box>
              <Button variant="normal" onClick={() => onRequestContributor?.(item.id)}>
                Request to be a contributor
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </Container>
    </Box>
  );
});
